import React, { useContext, useEffect, useRef, useState } from "react";
// router
import { useNavigate, useLocation } from "react-router-dom";
// user
import { UserContext } from "@src/App";
// hooks
import { useInterWindow } from "@src/utils/hooks/useInterWindow";
import { useBroadcastChannel } from "@src/utils/hooks/useBroadcastChannel";
// review
import { ReviewProviderData } from "./Provider";
import { ReviewTab, ReviewBlindInfo, ReviewBranchCheck } from "./ControlPanel";
// components
import CommunityActionModal from "@src/components/community/CommunityActionModal";
import { ReviewCommunityWrap, BlogReviewCommunityWrap } from "./Wrap";
import Blank from "@src/components/common/blank/Blank";
import BottomFixedModal from "@src/components/common/bottomFixedModal/BottomFixedModal";
import NotiText from "@src/components/common/notiText/NotiText";
import LoadingSpinner from "@src/components/common/loading/LoadingSpinner";
// images
import IconInfoCircle from "@assets/images/common/community/icon-info-circle@3x.png";

// 리뷰 영역
const ReviewListSection = ({ activeParentTabId }: { activeParentTabId: number }) => {
  // context
  const {
    partner,
    communityList,
    setCommunityList,
    reviewDataDetail,
    setReviewDataDetail,
    communityModalData,
    reviewImageData,
    setReviewImageData,
    filter,
    setFilter,
    activeReviewTab,
    setRefreshFlag,
    filterModalIsOpen,
    setFilterModalIsOpen,
    refreshFlag,
  } = useContext(ReviewProviderData).data;

  // 베스트순 안내 팝업
  const [bestInfoCircleIsOpen, setBestInfoCircleIsOpen] = useState<boolean>(false);

  // 초기 스크롤 위치
  const initialScrollTop = useRef(0);

  // 컴포넌트 참조
  const reviewListSectionRef = useRef(null);

  // // 컴포넌트 마운트시 스크롤 위치 계산
  useEffect(() => {
    if (reviewListSectionRef?.current) {
      const offsetTop = reviewListSectionRef.current.offsetTop || 0;
      initialScrollTop.current = offsetTop - 106;
    }
  }, [reviewListSectionRef]);

  // 리뷰 상세 모달 열기 설정
  const { openWindow: openReviewDetail } = useInterWindow({
    id: "reviewListSection",
    receiverId: "reviewDetailSection",
    callBackWhenBackToOriginalWindow: () => {
      setReviewDataDetail(null);
      if (notifyStatus.current) {
        notifyStatus.current = false;
        setRefreshFlag(true);
      }
    },
  });

  // 리뷰 이미지 모달 열기 설정
  const { openWindow: openReviewImage } = useInterWindow({
    id: "reviewListSection",
    receiverId: "image",
    callBackWhenBackToOriginalWindow: () => {
      setReviewImageData(null);
    },
  });

  // user
  const user = useContext(UserContext);

  // history
  const navigate = useNavigate();
  const location = useLocation();

  // iOS 체크
  const isiOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  // 리뷰 UUID
  const reviewUUid = useRef<string | null>(null);

  // 리뷰 상세 데이터 업데이트
  const notifyStatus = useRef<boolean>(false);

  // 새로운 리뷰 상세 데이터(리뷰 상세에서 전달받는 용도)
  const [newReviewDataDetail, setNewReviewDataDetail] = useState<any>(null);

  // AOS용 커뮤니티 모달, 필터 모달 닫기
  useEffect(() => {
    if (location && !location?.state) {
      setFilterModalIsOpen(false);
      setCommunityModalOpen(false);
      setBestInfoCircleIsOpen(false);
    }
  }, [location]);

  // 리뷰 상세 데이터 수신
  const { broadcastMessage: broadcastReviewMessage } = useBroadcastChannel(
    "review",
    (e: MessageEvent) => {
      if (e?.data?.type === "sendUpdatedDataToSender") {
        // 리뷰 상세에서 데이터 업데이트
        const { newReviewData } = e.data;
        setNewReviewDataDetail(newReviewData);
      } else if (e?.data?.type === "notifySenderForDataUpdate") {
        // 리뷰 상세에서 리스트 갱신 필요 신호
        notifyStatus.current = true;
      }
    },
  );

  // 리뷰 상세 데이터 업데이트
  useEffect(() => {
    if (newReviewDataDetail) {
      if (!newReviewDataDetail?.reviewData) return;
      setCommunityList((prev) => {
        return prev.map((item) => {
          if (item.uuid === newReviewDataDetail?.reviewData?.uuid) {
            return { ...item, ...newReviewDataDetail?.reviewData };
          }
          return item;
        });
      });
      setNewReviewDataDetail(null);
    }
  }, [newReviewDataDetail]);

  // 필터 모달 닫기시 베스트순 안내 팝업 닫기
  useEffect(() => {
    if (!filterModalIsOpen) {
      setBestInfoCircleIsOpen(false);
    }
  }, [filterModalIsOpen]);

  // 리뷰 상세 데이터 여부에 따라 리뷰 상세 모달 열기
  useEffect(() => {
    if (reviewDataDetail?.id) {
      openReviewDetail(
        {
          partner: partner,
          reviewData: reviewDataDetail,
        },
        {
          url: `/partner/${partner.uuid}/review/${reviewDataDetail?.reviewData.uuid}`,
          hidetop: true,
          hidesafearea: false,
        },
      );
    }
  }, [reviewDataDetail?.id]);

  // 리뷰 이미지 데이터에 따라 리뷰 이미지 모달 열기
  useEffect(() => {
    if (reviewImageData?.id) {
      openReviewImage(
        {
          photoData: reviewImageData,
        },
        {
          url: `/image`,
          hidetop: true,
          hidesafearea: false,
        },
      );
    }
  }, [reviewImageData?.id]);

  // 커뮤니티 모달 데이터에 따라 커뮤니티 모달 열기
  const [communityModalOpen, setCommunityModalOpen] = useState(false);
  useEffect(() => {
    if (communityModalData?.id) {
      reviewUUid.current = communityModalData.reviewUuid;
      setCommunityModalOpen(true);
      if (!isiOS) {
        // AOS용 모달 열기
        navigate(".", { state: { communityModal: true } });
      }
    }
  }, [communityModalData?.id]);

  // 필터 모달 열림 (AOS용)
  useEffect(() => {
    if (!isiOS && filterModalIsOpen) {
      navigate(".", { state: { filterModal: true } });
    }
  }, [filterModalIsOpen]);

  // 갱신 플래그 시 갱신 플래그 초기화 (리스트 갱신은 Wrap에서 처리)
  useEffect(() => {
    if (refreshFlag) {
      setRefreshFlag(false);
      reviewUUid.current = null;
    }
  }, [refreshFlag]);

  // 로딩 스피너 노출 여부
  const [showLoader, setShowLoader] = useState<boolean>(false);

  return (
    <>
      <div ref={reviewListSectionRef}>
        <Blank height={20} />
        <div className="review-list-wrap review-basic-wrap">
          {user?.me && (
            <>
              {/* 리뷰 탭 */}
              <ReviewTab />
              {/* 커뮤니티 리뷰일 때만 지점/블라인드 정보 표시 */}
              {activeReviewTab === 0 && <ReviewBranchCheck />}
              {activeReviewTab === 0 && <ReviewBlindInfo />}
              {/* 커뮤니티 리뷰 리스트 */}
              <ReviewCommunityWrap
                activeParentTabId={activeParentTabId}
                initialScrollTop={initialScrollTop.current}
                showLoader={(state: boolean) => setShowLoader(state)}
              />
              {/* 블로그 리뷰 리스트 */}
              <BlogReviewCommunityWrap
                activeParentTabId={activeParentTabId}
                initialScrollTop={initialScrollTop.current}
                showLoader={(state: boolean) => setShowLoader(state)}
              />
            </>
          )}
        </div>
      </div>
      {showLoader && <LoadingSpinner />}
      {/* 커뮤니티 모달(신고/차단) */}
      {communityModalData && (
        <CommunityActionModal
          isShow={!!communityModalOpen}
          category={communityModalData.category}
          communityUuid={communityModalData.communityUuid}
          customerUuid={communityModalData.customerUuid}
          clearData={(result) => {
            if (!isiOS) {
              navigate(-1);
            }
            setCommunityModalOpen(false);
            if (result) {
              setRefreshFlag(true);
            }
          }}
        />
      )}
      {/* 필터(최신순/베스트순) 모달 */}
      <BottomFixedModal
        isShow={!!filterModalIsOpen}
        hideModal={() => {
          if (!isiOS) navigate(-1);
          setFilterModalIsOpen(false);
        }}
        title={"정렬"}
        overlayBgColor={"rgba(0, 0, 0, .5)"}
        isTransition={true}
        className="review-filter-modal "
      >
        <div>
          {bestInfoCircleIsOpen && filterModalIsOpen && (
            <div className="review-noti-style">
              <NotiText
                text="업체가 가장 추천하는 리뷰로, 업체의 특징을 가장 잘 보여주는 리뷰가 상위로 노출됩니다. "
                color="blue"
              />
            </div>
          )}
          <ul className="bottom-modal-content-list">
            <li
              className={filter === "베스트순" ? "on" : ""}
              onClick={(e: React.MouseEvent) => {
                setFilter("베스트순");
                setBestInfoCircleIsOpen(false);
                if (!isiOS) navigate(-1);
                setFilterModalIsOpen(false);
              }}
            >
              베스트순{" "}
              <img
                src={IconInfoCircle}
                width={18}
                alt={"베스트순"}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  setBestInfoCircleIsOpen((prev) => !prev);
                }}
              />
              <i className="icon icon-checkmark-brown500"></i>
            </li>
            <li
              className={filter === "최신순" ? "on" : ""}
              onClick={() => {
                setFilter("최신순");
                setBestInfoCircleIsOpen(false);
                if (!isiOS) navigate(-1);
                setFilterModalIsOpen(false);
              }}
            >
              최신순<i className="icon icon-checkmark-brown500"></i>
            </li>
          </ul>
        </div>
      </BottomFixedModal>
    </>
  );
};

export default ReviewListSection;
